/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import * as Sentry from '@sentry/react-native';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { AppUpdateConfig } from '../session/bootstrapMobileSessionService';
import { getAppVersion, getStoreUrl } from '../session/appVersionGate';

type ForceUpdateScreenProps = {
  config: AppUpdateConfig;
};

export function ForceUpdateScreen({ config }: ForceUpdateScreenProps) {
  const storeUrl = getStoreUrl(config);
  const currentVersion = getAppVersion();
  const minVersion = Platform.OS === 'ios' ? config.min_version_ios : config.min_version_android;
  const [openFailed, setOpenFailed] = useState(false);

  useEffect(() => {
    Sentry.addBreadcrumb({
      type: 'navigation',
      category: 'ui.force_update',
      message: 'force_update_blocked',
      level: 'info',
      data: {
        current_version: currentVersion,
        min_version: minVersion,
        has_store_url: storeUrl != null,
      },
    });
  }, [currentVersion, minVersion, storeUrl]);

  const handleUpdate = async () => {
    if (!storeUrl) return;
    try {
      await Linking.openURL(storeUrl);
    } catch (e) {
      console.warn('[ForceUpdate] openURL failed', e);
      setOpenFailed(true);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <Text style={styles.title}>Actualización requerida</Text>
        <Text style={styles.message}>{config.message}</Text>
        {storeUrl ? (
          <TouchableOpacity
            style={styles.button}
            onPress={handleUpdate}
            accessibilityRole="button"
            accessibilityLabel="Actualizar ahora"
          >
            <Text style={styles.buttonText}>Actualizar ahora</Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.fallbackHint}>
            Buscá "Vuelto Negocio" en la tienda de tu dispositivo.
          </Text>
        )}
        {openFailed ? (
          <Text style={styles.fallbackHint}>
            No se pudo abrir la tienda. Buscá "Vuelto Negocio" manualmente o escribinos a
            soporte@vuelto.ar.
          </Text>
        ) : null}
        <TouchableOpacity
          style={styles.secondaryLink}
          onPress={() => void Linking.openURL('https://wa.me/5491133445566')}
          accessibilityRole="link"
        >
          <Text style={styles.secondaryLinkText}>¿Problemas para actualizar? Escribinos</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ticketColors.counter,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  card: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    padding: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: ticketColors.ink,
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    color: ticketColors.ink2,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 21,
  },
  button: {
    backgroundColor: ticketColors.gold,
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 10,
    width: '100%',
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  fallbackHint: {
    fontSize: 14,
    color: ticketColors.ink3,
    textAlign: 'center',
  },
  secondaryLink: {
    marginTop: 16,
    paddingVertical: 8,
  },
  secondaryLinkText: {
    fontSize: 14,
    color: ticketColors.gold,
    textAlign: 'center',
  },
});
